/* eslint-disable react/prop-types */
import React from 'react';
import { useSelector } from 'react-redux';
import CancelIcon from '@mui/icons-material/Cancel';
import produce from 'immer';
import moment from 'moment-timezone';

import { Box, Checkbox, InputAdornment, TextField } from '@mui/material';

import { AutocompleteWithChips, NumberInput } from '../../../../components/Inputs';
import localization from '../../../../localization';
import { countriesSelector } from '../../../../redux/selectors/SessionData';
import {
  PRICE_PROPERTIES,
  PRICE_PROPERTIES as PROPERTIES,
  REQUIRED_PRICE_PROPERTIES,
} from './constants';
import { checkDateRangeError, checkRequiredPropertyError, defaultCountryErrorCheck, getCopyPrice } from './util';

const FIELDS_LABEL_MAP = {
  [PRICE_PROPERTIES.UP_SELL]: 'upsellPrice',
  [PRICE_PROPERTIES.CROSS_SELL]: 'crossSellPrice',
};

const PriceNumberField = ({ propertyName, currentPriceData, onCurrentPricePropertyChange }) => {
  const value = currentPriceData[propertyName];
  const isRequired = REQUIRED_PRICE_PROPERTIES.includes(propertyName);
  const hasError = isRequired && checkRequiredPropertyError(value);

  const handleInputChange = (e) => {
    const newValue = e.target.value.trim();

    if (/^[0-9]+(\.[0-9]{1,2})?$/.test(`${newValue}`)) {
      onCurrentPricePropertyChange(propertyName, +e.target.value);

      return;
    }

    if (!newValue) {
      onCurrentPricePropertyChange(propertyName);
    }
  };

  return (
    <NumberInput
      name={propertyName}
      isRequired={isRequired}
      hasError={hasError}
      minMAx={{ min: 0 }}
      label={FIELDS_LABEL_MAP[propertyName] || propertyName}
      value={currentPriceData[propertyName] || ''}
      onChangeInput={handleInputChange}
    />
  );
};

const defaultValue = { id: 'default', value: 'default' };

const PriceCountryField = ({
  propertyName,
  currentPriceData,
  currentPricesData,
  setCurrentPrices,
  rowPrices,
}) => {
  const countriesOptions = useSelector(countriesSelector);
  const rowPricesIds = rowPrices.map(({ id, uid }) => id || uid);
  const rowCountries = rowPrices.map(({ country }) => country || defaultValue.id);

  const usedCountriesOptions = currentPricesData
    .filter(({ currency }) => currency === currentPriceData.currency)
    .map(({ country }) => ({ id: country || defaultValue.id }));

  const handleCountryChange = (newCountries) => {
    setCurrentPrices(
      produce((draft) => {
        // in case remove
        if (rowCountries.length > newCountries.length) {
          const removedItem = rowPrices.find(
            ({ country = 'default' }) => !newCountries.includes(country),
          );
          const removedId = removedItem?.id || removedItem?.uid;

          if (removedId) {
            const removedIndex = draft.findIndex(({ id, uid }) => [uid, id].includes(removedId));
            draft.splice(removedIndex, 1);

            return;
          }
        }

        const timestamp = new Date().getTime();

        draft.forEach((price) => {
          if (rowPricesIds.includes(price.id || price.uid)) {
            price.startDate = Math.max(price.startDate, timestamp);
          }
        });

        const newCountry = newCountries.find((country) => !rowCountries.includes(country));

        const newPrice = {
          ...getCopyPrice(currentPriceData),
          startDate: Math.max(currentPriceData.startDate, timestamp),
          country: newCountry === defaultValue.id ? undefined : newCountry,
        };

        draft.push(newPrice);
      }),
    );
  };

  return (
    <AutocompleteWithChips
      arrayTypeValue
      label={propertyName}
      usedOptions={usedCountriesOptions}
      selectOptions={[defaultValue, ...countriesOptions]}
      optionLabelKey='value'
      arrayValue={rowCountries}
      onChange={handleCountryChange}
      disableClearable
    />
  );
};

const PriceDateField = ({ propertyName, currentPriceData, onCurrentPricePropertyChange }) => {
  const value = currentPriceData[propertyName];
  const formattedValue = value && moment(value).format('YYYY-MM-DDTHH:mm');
  const isRequired = REQUIRED_PRICE_PROPERTIES.includes(propertyName);
  const hasError = (isRequired && checkRequiredPropertyError(value)) || checkDateRangeError(currentPriceData);

  const handleDateChange = (e) => {
    const newValue = e.target.value;
    const formatedNewValue = newValue ? new Date(newValue).getTime() : '';

    onCurrentPricePropertyChange(propertyName, formatedNewValue);
  };

  const inputProps = {
    min: moment().format('YYYY-MM-DDTHH:mm'),
    max: '2125-12-31T23:59',
  };

  if (PRICE_PROPERTIES.START_DATE === propertyName && currentPriceData[PROPERTIES.END_DATE]) {
    inputProps.max = moment(currentPriceData[PROPERTIES.END_DATE]).format('YYYY-MM-DDTHH:mm');
  }

  if (PRICE_PROPERTIES.END_DATE === propertyName && currentPriceData[PROPERTIES.START_DATE]) {
    inputProps.min = moment(currentPriceData[PROPERTIES.START_DATE]).format('YYYY-MM-DDTHH:mm');
  }

  return (
    <TextField
      label={localization.t(`labels.${propertyName}`)}
      type='datetime-local'
      error={hasError}
      required={isRequired}
      name={propertyName}
      variant='outlined'
      value={formattedValue || ''}
      InputLabelProps={{ shrink: true }}
      onChange={handleDateChange}
      inputProps={inputProps}
    />
  );
};

const PriceCheckboxField = ({ propertyName, currentPriceData, onCurrentPricePropertyChange }) => {
  const value = currentPriceData[propertyName];

  const handleCheckboxChange = () => {
    onCurrentPricePropertyChange(propertyName, !value);
  };

  return (
    <Checkbox
      checked={!!currentPriceData[propertyName]}
      onChange={handleCheckboxChange}
      name='vatIncluded'
      color='primary'
    />
  );
};

const PriceCurrencyField = ({ propertyName, currentPricesData, currentPriceData }) => {
  const { currency } = currentPriceData;
  const defaultCountryError = defaultCountryErrorCheck(currentPricesData, currency);

  return (
    <>
      <span
        style={
          defaultCountryError
            ? {
                color: '#ff6341',
              }
            : {}
        }
      >
        {currentPriceData[propertyName]}
      </span>
      {defaultCountryError && (
        <Box
          style={{
            color: '#ff6341',
            fontSize: '12px',
            fontStyle: 'italic',
            textAlign: 'center',
          }}
        >
          {localization.t('errorNotifications.defaultPriceIsRequired')}
        </Box>
      )}
    </>
  );
};

const PricesPropertyField = (property) => {
  const { propertyName } = property;

  switch (propertyName) {
    case PRICE_PROPERTIES.VALUE:
    case PRICE_PROPERTIES.MSRP:
    case PRICE_PROPERTIES.CROSS_SELL:
    case PRICE_PROPERTIES.UP_SELL:
      return <PriceNumberField {...property} />;
    case PRICE_PROPERTIES.COUNTRY:
      return <PriceCountryField {...property} />;
    case PRICE_PROPERTIES.START_DATE:
    case PRICE_PROPERTIES.END_DATE:
      return <PriceDateField {...property} />;
    case PRICE_PROPERTIES.VAT_INCLUDED:
      return <PriceCheckboxField {...property} />;
    case PRICE_PROPERTIES.CURRENCY:
      return <PriceCurrencyField {...property} />;
    default:
      return null;
  }
};

export default PricesPropertyField;
